import { ADMIN_PATH_BASE } from "@/lib/admin-config";
import { CODING_SUBJECTS, THEORY_SUBJECTS } from "@/data/subject-posts";

/**
 * 페이지 레이아웃 분기와 사이드바 광고 노출 정책의 단일 진입점.
 *
 * LayoutSwitch 의 셸 분기와 AdSenseWrapper.tsx 의 사이드바 노출 정책이 따로 관리되면 한쪽만 갱신되어
 * 회귀가 반복됨 (AdFit → AdSense 전환 시 c-language 등 누락). 이 파일에서 단일 정의 후 공유함.
 */

/** YAML 관리자 미리보기만 공용 풀이 셸을 사용한다. 분석 대화상자는 자체 레이아웃을 유지한다. */
export function isAdminProblemPreviewPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === `${ADMIN_PATH_BASE}/problem-search` ||
    new RegExp(`^${ADMIN_PATH_BASE}/problems_[^/]+$`).test(path);
}

/** 과목 목록 페이지 — 사이드바 광고 미노출. */
export const SUBJECT_PATHS = [
  "/coding/c-language",
  "/coding/java",
  "/coding/python",
  "/coding/sql",
  "/coding/common",
  "/theory/db",
  "/theory/network-os",
  "/theory/security-newtech",
  "/theory/sw-design",
  "/theory/sw-dev",
  "/past-exam",
] as const;

/** 오답노트 — 사이드바 광고 미노출. */
export const ERROR_NOTES_PATH = "/my/error-notes";

/** 학습 일정 캘린더 — 우측 맥락 컬럼 없이 본문을 넓게 쓰는 /my 탭(JCG-2310). */
export const SCHEDULE_PATH = "/my/schedule";

export function isSchedulePage(pathname: string): boolean {
  return normalizePath(pathname) === SCHEDULE_PATH;
}

/** 커뮤니티 Hero (정확 일치). 사이드바 광고 미노출. */
export const COMMUNITY_PATH = "/community";

/** 복원 게시판 에디터 (하위 경로 포함). 사이드바 광고 미노출. */
export const RESTORATION_PATH_PREFIX = "/community/restoration";

/**
 * 이슈 게시판(`/community/issues`, `/new`, `/<id>`) — custom 셸 적용 대상(JCG-3029).
 *
 * 포럼 인덱스(/forum/jcg)·/my 대시보드처럼 지도·점수·풀이 같은 고정 학습 도구가 없고, 페이지가
 * 좌(상태·종류 필터 네비 + 새 이슈 CTA)/우(이용 안내·요약본 최신 버전·이슈 정보·진행 이력) 사이드바를
 * ArticleShell 의 custom 슬롯에 직접 주입한다(`IssueBoardShell`). prefix 매칭이라 세 라우트가 한 셸을
 * 공유하고, `/community`(담벼락 셸)·`/community/restoration`(복원 셸)과는 경로가 서로소다.
 * 모바일/태블릿(<1024px)은 클래식 단일 컬럼 폴백.
 */
export const COMMUNITY_ISSUES_PATH_PREFIX = "/community/issues";

/** 이슈 게시판 custom 셸 상단바(ContentHeader)에 상시 표시할 제목. */
export const COMMUNITY_ISSUES_SHELL_LABEL = "질문·버그 게시판";

export function isCommunityIssuesPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  return (
    path === COMMUNITY_ISSUES_PATH_PREFIX ||
    path.startsWith(COMMUNITY_ISSUES_PATH_PREFIX + "/")
  );
}

/**
 * 감자단 잡담(`/community/talk`, `/new`, `/<id>`) — custom 셸 적용 대상(JCG-3029).
 * 좌: 목록(전체글·추천글·내 글)·정렬·다른 게시판 네비, 우: 글쓰기 CTA·추천글 TOP(최대 5)·이용 안내.
 * 페이지가 `CommunityBoardShell` 로 슬롯에 주입한다. 모바일(<1024px)은 단일 컬럼 폴백.
 */
export const COMMUNITY_TALK_PATH_PREFIX = "/community/talk";

/** 감자단 잡담 custom 셸 상단바(ContentHeader) 상시 제목. */
export const COMMUNITY_TALK_SHELL_LABEL = "감자단 잡담";

export function isCommunityTalkPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === COMMUNITY_TALK_PATH_PREFIX || path.startsWith(COMMUNITY_TALK_PATH_PREFIX + "/");
}

/**
 * 인덱스 셸 페이지 — 좌/우 사이드바 콘텐츠를 페이지가 직접 주입하는 3컬럼 셸(JCG-2104).
 *
 * 이론/코딩 글(isArticlePage)·기출 풀이(isExamShellPage)와 달리 지도·점수·풀이 같은 고정
 * 학습 도구가 없고, 페이지가 ArticleShell 의 custom 좌/우 portal 슬롯에 자체 콘텐츠(네비·미리보기
 * 등)를 그린다. ArticleShell 은 variant="custom" 으로 단순 렌더 경로를 탄다(LayoutSwitch 가 판정).
 *
 * - /forum/jcg: 문제 포럼 인덱스(좌: 과목/scope 네비, 중앙: ID 그리드, 우: 호버 미리보기).
 *
 * 하위 토론 페이지(/forum/jcg/[subject]/[scope]/[problemId])는 인덱스가 아니므로 미포함.
 */
const INDEX_SHELL_PATHS = ["/forum/jcg"] as const;

/** 인덱스 셸 페이지의 상단바(ContentHeader) 상시 제목. */
const INDEX_SHELL_LABELS: Record<string, string> = {
  "/forum/jcg": "문제 포럼",
};

/**
 * 마이페이지 통합 대시보드(/my) — custom 셸 페이지(JCG-2073).
 *
 * 이론/코딩 글(isArticlePage)·기출 풀이(isExamShellPage)와 달리 지도·점수·풀이 같은 고정
 * 학습 도구가 없고, ArticleShell variant="custom" 으로 단순 렌더 경로를 탄다. 좌측 사이드바엔
 * 공유 "내 메뉴" 탭 네비를, 우측엔 탭별 맥락 카드를 페이지가 직접 portal 로 주입한다.
 *
 * /my 와 그 하위 6개 라우트(study-progress·error-notes·badges·notes·pass·purchases)를 모두
 * 셸 대상으로 삼는다 — prefix 매칭이라 새 하위 탭을 추가해도 자동 포함된다.
 */
const MY_DASHBOARD_PREFIX = "/my";

/** 구매한 요약 PDF의 버전별 변경사항 상세 페이지(JCG-3242). */
export const PDF_VERSION_DETAIL_PREFIX = "/my/purchases/pdf";

/** /my 통합 셸 상단바(ContentHeader)에 상시 표시할 탭 제목. */
const MY_DASHBOARD_LABELS: Record<string, string> = {
  "/my": "내 메뉴",
  "/my/study-progress": "공부 진행상황",
  "/my/schedule": "학습 일정",
  "/my/error-notes": "나의 오답노트",
  "/my/badges": "도전과제",
  "/my/notes": "필기 모음",
  "/my/bookmarks": "즐겨찾기",
  "/my/comments": "내 댓글",
  "/my/pass": "감자 이용권",
  "/my/purchases": "구매 목록",
  "/my/refund-request": "첨부 이미지 유실로 인한 환불 요청",
};

/** 사이드바 광고를 추가로 숨길 정확 일치 경로 (max-width 분기와 무관한 정책적 차단). */
const SIDEBAR_AD_HIDDEN_EXTRA_PATHS = [
  "/",
  "/theory",
  "/coding",
  "/membership",
  "/exam-registration/jcg-test-center",
  // past-exam 연도별 회차 인덱스 페이지 — 데스크탑은 카드형 IndexShell(JCG-2164),
  // 모바일/태블릿 폴백에서도 카드 그리드가 좁아지지 않도록 광고 미노출(다른 CARD_INDEX_SHELL 멤버와 동일 정책).
  // 루트 `/past-exam` 은 SUBJECT_PATHS(isWideLayoutPage)로 이미 숨겨짐.
  "/past-exam/2026",
  "/past-exam/2025",
  "/past-exam/2024",
  "/past-exam/2023",
  "/past-exam/2022",
  "/past-exam/2021",
  "/past-exam/2020",
  // 법적 정적 문서 — rail-only 셸 대상이라 광고도 함께 차단(JCG-2165).
  "/privacy",
  "/terms",
] as const;

/** 사이드바 광고를 추가로 숨길 경로 prefix. */
const SIDEBAR_AD_HIDDEN_EXTRA_PREFIXES = [
  "/sign-in",
  "/sign-up",
  // 계정 관리(Clerk UserProfile) catch-all — rail-only 셸 대상이라 광고도 차단(JCG-2165).
  "/user-profile",
  "/summary-pdf",
  // 이슈 게시판(목록·작성·상세) — custom 셸(좌 필터 네비·우 안내 카드)이라 사이드바 광고 미노출(JCG-3029).
  "/community/issues",
  // 감자단 잡담(목록·작성·상세) — custom 셸(좌 네비·우 카드)이라 사이드바 광고 미노출(JCG-3029).
  "/community/talk",
  ADMIN_PATH_BASE,
] as const;

export function isSubjectPage(pathname: string): boolean {
  return (SUBJECT_PATHS as readonly string[]).includes(pathname);
}

export function isErrorNotesPage(pathname: string): boolean {
  return pathname === ERROR_NOTES_PATH;
}

export function isCommunityPage(pathname: string): boolean {
  return pathname === COMMUNITY_PATH;
}

/**
 * community 대시보드 셸(JCG-2106) 적용 대상 — `/community` 정확 일치.
 * 복원 게시판(`/community/restoration/*`)은 자체 풀폭 레이아웃이라 제외.
 * 데스크탑일 때 LayoutSwitch 는 상단 NAV·광고를 미렌더하고 footer·globals 만 뒤에 두며,
 * 셸 그리드(좌 후보투표·중앙 히어로·우 후기피드)는 CommunityClient 가 직접 소유한다.
 */
export function isCommunityShellPage(pathname: string): boolean {
  return normalizePath(pathname) === COMMUNITY_PATH;
}

export function isRestorationPage(pathname: string): boolean {
  return pathname.startsWith(RESTORATION_PATH_PREFIX);
}

/** 합격률 추이 미니 차트를 좌측 사이드바(목차 위)에 노출할 페이지(JCG-2101). */
export const PASS_RATE_PATH = "/exam-registration/jeongcheogi-pass-rate";

/** 시험 안내·회차 후기는 광고 없이 공개 목차와 시험 준비 정보를 제공한다. */
export function isExamInfoPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path.startsWith("/exam-registration/") ||
    /^\/past-exam\/\d{4}\/\d{4}-\d+-review$/.test(path);
}

/** 원서 접수 체크리스트를 좌측 사이드바(목차 위)에 노출할 페이지(JCG-2101). */
export const APPLICATION_PATH = "/exam-registration/jeongcheogi-application";

/** 시험장 찾기 지도 서비스 배너를 좌측 사이드바 최상단에 노출할 페이지(JCG-2311). */
export const PRE_APPLICATION_PATH =
  "/exam-registration/jeongcheogi-pre-application";

/**
 * 합격률 페이지 여부 — 좌측 사이드바 목차 위에 실기 합격률 추이 미니 차트를 노출(JCG-2101/2109).
 * normalizePath 로 trailing-slash 를 흡수해 ArticleShell 인라인 판정을 단일화한다.
 */
export function isPassRatePage(pathname: string): boolean {
  return normalizePath(pathname) === PASS_RATE_PATH;
}

/**
 * 원서 접수 페이지 여부 — 좌측 사이드바 목차 위에 원서 접수 체크리스트를 노출(JCG-2101/2109).
 * normalizePath 로 trailing-slash 를 흡수해 ArticleShell 인라인 판정을 단일화한다.
 */
export function isApplicationPage(pathname: string): boolean {
  return normalizePath(pathname) === APPLICATION_PATH;
}

/**
 * 원서 사전입력 페이지 여부 — 좌측 사이드바 최상단에 시험장 찾기 배너를 노출(JCG-2311).
 * normalizePath 로 trailing-slash 를 흡수해 ArticleShell 인라인 판정을 단일화한다.
 */
export function isPreApplicationPage(pathname: string): boolean {
  return normalizePath(pathname) === PRE_APPLICATION_PATH;
}

export function isIndexShellPage(pathname: string): boolean {
  return (INDEX_SHELL_PATHS as readonly string[]).includes(normalizePath(pathname));
}

/** 인덱스 셸 상단바에 상시 표시할 페이지 제목(없으면 ""). */
export function getIndexShellLabel(pathname: string): string {
  return INDEX_SHELL_LABELS[normalizePath(pathname)] ?? "";
}

/**
 * /my 통합 대시보드(또는 그 하위 탭) 여부 — custom 셸 적용 대상(JCG-2073).
 * prefix 매칭이라 `/my`(인덱스) + `/my/<tab>` 전부 포함된다.
 */
export function isMyDashboardPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === MY_DASHBOARD_PREFIX || path.startsWith(MY_DASHBOARD_PREFIX + "/");
}

/** /my 셸 상단바에 상시 표시할 탭 제목(매핑 없으면 "내 메뉴"). */
export function getMyDashboardLabel(pathname: string): string {
  const path = normalizePath(pathname);
  if (isPdfVersionDetailPage(path)) return "PDF 버전 변경사항";
  return MY_DASHBOARD_LABELS[path] ?? "내 메뉴";
}

/**
 * /my 인덱스(대시보드 홈) 정확 일치 여부 (JCG-2073).
 * 인덱스 대시보드는 우측 맥락 사이드바 없이 2컬럼으로 렌더해 콘텐츠 폭을 최대화한다
 * (각 탭은 3컬럼 + 탭별 우측 맥락 카드 유지).
 */
export function isMyDashboardHome(pathname: string): boolean {
  return normalizePath(pathname) === MY_DASHBOARD_PREFIX;
}

/**
 * 우측 "맥락" 컬럼(custom 패널)을 두지 않는 /my 페이지 여부 — ArticleShell `hideRight` 단일 소스.
 *
 * - 대시보드 홈: 콘텐츠 폭 최대화를 위해 우측 맥락 카드 없음(JCG-2073).
 * - 오답노트: 탭/필터/팁을 좌측으로 옮기고(JCG-2060) 우측은 비워, 평소엔 2컬럼이다. 단
 *   풀이/오답노트를 열면 셸 솔루션 모드(rightMode==='solution')가 hideRight 보다 우선해
 *   우측에 풀이/오답노트 패널이 슬라이드되어 열린다(deriveRightColumn 우선순위) — 즉 평소 2컬럼,
 *   풀이 볼 때만 3컬럼. 빈 우측 컬럼이 상주하지 않는다.
 * - 학습 일정 캘린더(JCG-2310): 주/월 그리드가 가로 폭을 모두 쓰므로 우측 맥락 카드 없이 2컬럼.
 */
/**
 * 지난 회차 대시보드 스냅샷 뷰(`/my/snapshots/[sessionId]`) 여부 (JCG-3283).
 * 대시보드 홈 그리드를 읽기전용으로 미러링하므로 홈과 동일하게 우측 맥락 카드 없는 2컬럼으로 렌더한다.
 */
export function isMySnapshotPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  return (
    path === MY_DASHBOARD_PREFIX + "/snapshots" ||
    path.startsWith(MY_DASHBOARD_PREFIX + "/snapshots/")
  );
}

/** `/my/purchases/pdf/[productId]/[version]` 상세 경로 여부(JCG-3242). */
export function isPdfVersionDetailPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path.startsWith(PDF_VERSION_DETAIL_PREFIX + "/");
}

export function isMyTwoColumnPage(pathname: string): boolean {
  return (
    isMyDashboardHome(pathname) ||
    isMySnapshotPage(pathname) ||
    isPdfVersionDetailPage(pathname) ||
    isErrorNotesPage(pathname) ||
    isSchedulePage(pathname)
  );
}

/** 홈(/) 대시보드 셸 상단바(ContentHeader)에 상시 표시할 제목. */
export const HOME_SHELL_LABEL = "홈";

/**
 * 홈(`/`) 3컬럼 대시보드 셸 여부 — custom 셸 적용 대상(JCG-2110).
 *
 * 이론/코딩 글(isArticlePage)·기출 풀이(isExamShellPage)와 달리 지도·점수·풀이 같은 고정
 * 학습 도구가 없고, 페이지(children)가 좌(퀵 위젯: D-day·오늘의 감자시험) / 우(추천 글·배너)
 * 사이드바를 ArticleShell 의 custom 슬롯에 직접 주입한다. 본문(중앙)은 과목 탐색(SubjectBrowser)
 * + 기출 모아보기 CTA. 정확 일치(`/`)라 하위 경로를 오염시키지 않는다.
 *
 * 모바일/태블릿(셸 미적용 → inShell=false)은 기존 세로 랜딩 레이아웃으로 폴백한다.
 */
export function isHomeDashboardPage(pathname: string): boolean {
  return normalizePath(pathname) === "/";
}

export function isAdminFullWidth(pathname: string): boolean {
  return pathname.startsWith(ADMIN_PATH_BASE);
}

/* =========================================================
   데스크탑 기본 셸(RailShell) 상단바 제목 — rail-only (JCG-2111/2165/3679)
   ========================================================= */

/**
 * 데스크탑(≥1024px)에서 전용 셸 게이트(isShellPage·isCardIndexShellPage 등)에 걸리지 않는 경로는
 * 전부 LayoutSwitch 가 RailShell(전역 아이콘 레일 + 상단 제목 바, 좌/우 사이드바·광고 없음)로 렌더한다
 * (JCG-3679 — 데스크탑 클래식 레이아웃 없음). RailShell 은 본문 폭을 강제하지 않아 페이지 자체 max-w 를 쓴다.
 *
 * 아래 두 표는 레이아웃 게이트가 아니라 **상단바 제목**만 정한다. 표에 없는 경로는 빈 제목으로 렌더된다.
 * - 정확 일치: 하위 경로가 없는 단일 페이지. `/summary-pdf` 카탈로그·상세 랜딩은 별도 셸 대상이라
 *   대기자 폼·변경 이력의 정확 경로만 둔다(prefix 로 넣으면 하위 경로를 오염시킴).
 * - prefix 일치: 인증(Clerk catch-all `[[...sign-in]]`)·관리자처럼 하위 경로가 런타임에 생기거나 많은 경로.
 */
const RAIL_ONLY_SHELL_LABELS: Record<string, string> = {
  "/membership": "감자 이용권",
  "/summary-pdf/26-1-compressed-waitlist": "요약 PDF 출시 알림",
  "/summary-pdf/26-1-coding-waitlist": "요약 PDF 출시 알림",
  // 요약본 변경 이력(JCG-3029) — SUMMARY_PDF_DETAIL_EXCLUDE 에도 넣어 상세 랜딩 custom 셸이 오적용되지 않게 한다.
  "/summary-pdf/changelog": "요약본 변경 이력",
  "/privacy": "개인정보처리방침",
  "/terms": "이용약관",
  "/email/unsubscribe": "이메일 수신거부",
  "/youtube-verify": "유튜브 구독 인증",
  "/sentry-example-page": "Sentry 테스트",
};

/** prefix 매칭 상단바 제목. `[prefix, label]` 순서로 첫 매칭을 쓰고, 하위 경로도 같은 제목을 유지한다. */
const RAIL_ONLY_SHELL_PREFIX_LABELS: ReadonlyArray<readonly [string, string]> = [
  ["/sign-in", "로그인"],
  ["/sign-up", "회원가입"],
  ["/user-profile", "계정"],
  [ADMIN_PATH_BASE, "관리자"],
];

/** 404(not-found) 화면의 RailShell 상단바 제목 (JCG-3679). 경로와 무관하게 LayoutSwitch 가 직접 넘긴다. */
export const NOT_FOUND_SHELL_LABEL = "페이지를 찾을 수 없음";

/** path 가 prefix 와 정확 일치하거나 그 하위 경로(`prefix/...`)인지. */
function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(prefix + "/");
}

/** RailShell 상단바 제목이 등록된 경로인지 (= getRailOnlyShellLabel 이 빈 문자열이 아님). */
export function isRailOnlyShellPage(pathname: string): boolean {
  return getRailOnlyShellLabel(pathname) !== "";
}

/** RailShell 상단바에 상시 표시할 페이지 제목(없으면 ""). */
export function getRailOnlyShellLabel(pathname: string): string {
  const path = normalizePath(pathname);
  const exact = RAIL_ONLY_SHELL_LABELS[path];
  if (exact) return exact;
  const prefixMatch = RAIL_ONLY_SHELL_PREFIX_LABELS.find(([prefix]) =>
    matchesPrefix(path, prefix),
  );
  return prefixMatch ? prefixMatch[1] : "";
}

/* =========================================================
   요약 PDF 상품 상세 랜딩 — custom 셸(좌 섹션 앵커 / 우 sticky 구매 CTA) (JCG-2121)
   ========================================================= */

/** 요약 PDF 경로 prefix. */
const SUMMARY_PDF_BASE = "/summary-pdf";

/** 요약 PDF 카탈로그(정확 일치) — 카드형 IndexShell 대상(JCG-2120). 상세 게이트에서 제외. */
const SUMMARY_PDF_CATALOG = "/summary-pdf";

/**
 * 요약 PDF 상세 랜딩에서 제외할 하위 경로(정확 일치) — 대기자 폼 2종은 rail-only 셸(JCG-2122).
 * 새 대기자/특수 하위 페이지가 생기면 여기에 추가해 상세 셸이 잘못 적용되지 않게 한다.
 */
const SUMMARY_PDF_DETAIL_EXCLUDE = [
  "/summary-pdf/26-1-compressed-waitlist",
  "/summary-pdf/26-1-coding-waitlist",
  // 요약본 변경 이력 — rail-only 셸(JCG-3029).
  "/summary-pdf/changelog",
] as const;

/**
 * 요약 PDF 상품 상세 랜딩 여부 — custom 셸 적용 대상(JCG-2121).
 *
 * `/summary-pdf/` 하위 prefix 이되 **카탈로그(정확 `/summary-pdf`)·대기자 2종은 제외**한다.
 * 카탈로그는 카드형 IndexShell(isCardIndexShellPage, JCG-2120), 대기자는 rail-only
 * (isRailOnlyShellPage, JCG-2122)로 각각 분기되며, LayoutSwitch 가 isShellPage(상세) 다음에
 * 그 둘을 보지 않도록 분기 순서가 카탈로그·대기자가 상세보다 절대 먼저 매칭되지 않게 설계됐다
 * (셋은 경로가 상호 배타라 충돌 없음 — 아래 게이트가 카탈로그·대기자를 명시 제외).
 *
 * 긴 세일즈 랜딩이라 ArticleShell variant="custom" 으로 좌측엔 섹션 앵커 scroll-spy 네비
 * (미리보기/목차/특전/구매 방법), 우측엔 sticky 구매 CTA(ProductInfo 결제 박스)를 페이지가 슬롯
 * 주입한다. 데스크탑(≥1024px)에서만 셸, 모바일/태블릿은 기존 클래식 풀블리드 랜딩 폴백.
 */
export function isSummaryPdfDetailPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (path === SUMMARY_PDF_CATALOG) return false; // 카탈로그(정확 일치) 제외
  if ((SUMMARY_PDF_DETAIL_EXCLUDE as readonly string[]).includes(path)) {
    return false; // 대기자 2종 제외
  }
  return path.startsWith(SUMMARY_PDF_BASE + "/");
}

/** 요약 PDF 상세 셸 상단바(ContentHeader)에 상시 표시할 제목. */
export const SUMMARY_PDF_DETAIL_LABEL = "요약 PDF";

/* =========================================================
   글(article) 페이지 — UI 2.0 3컬럼 셸 적용 대상 (JCG-2060)
   ========================================================= */

/**
 * 셸을 적용할 루트.
 * - coding·theory: 이론/코딩 본문 글(지도·출석 등 학습 도구 포함).
 * - exam-registration: 시험 접수 안내 콘텐츠. 셸은 적용하되 학습 도구는 제외(theory-expected-questions 동일, JCG-2101).
 * past-exam·community 등은 제외.
 */
const ARTICLE_ROOTS = ["coding", "theory", "exam-registration"] as const;

/** 지도·출석 등 "이론 학습 도구"가 어울리는 루트(coding·theory 본문 글에서만). */
const THEORY_LEARNING_ROOTS = ["coding", "theory"] as const;

/**
 * exam-registration 루트에서 셸을 적용하지 않을 슬러그.
 * jcg-test-center 는 PostLayout 이 아닌 자체 page.tsx(시험장 지도 클라이언트)라 셸 비적용.
 */
const EXAM_REGISTRATION_SHELL_EXCLUDE = ["jcg-test-center"] as const;

/**
 * 카테고리(인덱스) 경로 → breadcrumb 라벨.
 * SUBJECT_PATHS 의 coding/theory 멤버와 1:1 대응.
 */
export const SUBJECT_LABELS: Record<string, string> = {
  "/coding/c-language": "C언어",
  "/coding/java": "Java",
  "/coding/python": "Python",
  "/coding/sql": "SQL",
  "/coding/common": "공통 기초",
  "/theory/db": "데이터베이스",
  "/theory/network-os": "네트워크/OS",
  "/theory/security-newtech": "보안/신기술",
  "/theory/sw-design": "SW 설계",
  "/theory/sw-dev": "SW 개발",
  "/coding": "실기 코딩",
  "/theory": "실기 이론",
  "/exam-registration": "시험 접수",
};

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * 셸이 적용되는 "글/콘텐츠 페이지" 여부 — PostLayout 으로 렌더되는 본문.
 *
 * 매칭:
 * - 2세그먼트 `/coding/<slug>`·`/theory/<slug>` (단, 카테고리 인덱스 페이지 제외)
 * - 2세그먼트 `/exam-registration/<slug>` (시험 접수 안내 콘텐츠, jcg-test-center 등 자체 page 제외, JCG-2101)
 * - 3세그먼트 `/coding/<cat>/<slug>`·`/theory/<cat>/<slug>` (cat 은 알려진 카테고리)
 *
 * 제외: `/coding`·`/theory`·`/exam-registration` 루트, 카테고리 인덱스(SUBJECT_PATHS), past-exam, 그 외 루트.
 *
 * 유지보수 계약: `/coding`·`/theory`·`/exam-registration` 아래 PostLayout 이 아닌 2세그먼트 자식이
 * 새로 생기면 여기에 명시적 제외를 추가해야 셸이 잘못 적용되지 않음
 * (exam-registration 은 EXAM_REGISTRATION_SHELL_EXCLUDE 로 관리).
 */
export function isArticlePage(pathname: string): boolean {
  const path = normalizePath(pathname);
  const segments = path.split("/").filter(Boolean);

  // 루트 세그먼트가 coding/theory/exam-registration 가 아니면 제외
  if (!(ARTICLE_ROOTS as readonly string[]).includes(segments[0])) return false;

  // `/coding`, `/theory`, `/exam-registration` 루트 자체 제외
  if (segments.length < 2) return false;

  // 카테고리 인덱스 페이지(`/coding/c-language` 등) 제외
  if ((SUBJECT_PATHS as readonly string[]).includes(path)) return false;

  if (segments.length === 2) {
    // exam-registration 루트는 안내 콘텐츠 슬러그만 — jcg-test-center 등 자체 page.tsx 는 제외(JCG-2101)
    if (segments[0] === "exam-registration") {
      return !(EXAM_REGISTRATION_SHELL_EXCLUDE as readonly string[]).includes(segments[1]);
    }
    // `/coding/<slug>`, `/theory/<slug>` — 인덱스가 아니므로 글 페이지
    return true;
  }

  if (segments.length === 3) {
    // `/coding/<cat>/<slug>` — <cat> 이 알려진 카테고리일 때만 글 페이지
    const categoryPath = `/${segments[0]}/${segments[1]}`;
    return (SUBJECT_PATHS as readonly string[]).includes(categoryPath);
  }

  return false;
}

/** 모의고사 — 셸 적용 대상(점수·문제 목록). 프리미엄 풀 랜덤 출제 단일 페이지. */
const EXAM_SHELL_EXACT_PATHS = ["/mock-exam", "/final-mock-exam"] as const;

/** mock-exam 계열 정확 일치 페이지 여부. */
export function isMockExamShellPage(pathname: string): boolean {
  return (EXAM_SHELL_EXACT_PATHS as readonly string[]).includes(
    normalizePath(pathname),
  );
}

/**
 * 파이널 모의고사(/final-mock-exam) 정확 일치 페이지 여부 (JCG-2898).
 * 파이널 전용 처리(오팔 셸 본문·헤더 배경 등)의 단일 게이트. `isMockExamShellPage`와 동일하게
 * `normalizePath`를 거치므로 trailing-slash 등에서 사이드바(공유 게이트)와 패리티가 유지된다.
 */
export function isFinalMockExamPage(pathname: string): boolean {
  return normalizePath(pathname) === "/final-mock-exam";
}

/**
 * 기출·모의고사 "문제 풀이 페이지" 여부 — 3컬럼 셸 적용 대상(JCG-2066).
 *
 * 이론/코딩 글(isArticlePage)과 달리 RandomProblems 기반으로 점수 패널·문제 목록 네비를
 * 노출해야 하는 페이지. 본문은 문제뿐이라 셸 좌측은 지도/목차 대신 점수+문제 목록으로 대체된다.
 *
 * 매칭:
 * - `/mock-exam`, `/final-mock-exam` (정확 일치)
 * - `/past-exam/<YYYY>/<slug>` (회차 문제 페이지)
 *
 * 제외: `/past-exam`(루트)·`/past-exam/<YYYY>`(연도 인덱스) — 문제 풀이가 아닌 목록 페이지.
 */
export function isExamShellPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (isMockExamShellPage(path)) return true;
  const segments = path.split("/").filter(Boolean);
  return (
    segments[0] === "past-exam" &&
    segments.length === 3 &&
    /^\d{4}$/.test(segments[1])
  );
}

/**
 * "문제만 푸는" 페이지 여부 (JCG-2241) — mock-exam·final-mock-exam·past-exam 회차(isExamShellPage)에
 * 더해 gamja-exam(`*-gamja-exam`)·감자퀴즈(`*-quiz`) 슬러그까지 포함한다. 본문이 문제뿐이라 모바일에서
 * 스크롤 고정 핀 제목(StickyTitle)이 문제를 가리므로, 이런 페이지에서만 모바일 핀 제목을 숨기는 데 쓴다.
 */
export function isProblemSolvingPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (isExamShellPage(path)) return true;
  const lastSegment = path.split("/").filter(Boolean).pop() ?? "";
  return lastSegment.endsWith("-gamja-exam") || lastSegment.endsWith("-quiz");
}

/**
 * 카테고리 인덱스 페이지 여부 — 3컬럼 셸 적용 대상(JCG-2098).
 *
 * `/coding/c-language`·`/theory/db` 등 과목 글 목록(FolderGrid) 인덱스. SUBJECT_PATHS 중
 * past-exam(문제 풀이 목록, 셸 비대상)을 제외한 coding/theory 카테고리 10개.
 *
 * 셸에서 좌측 = 주요항목(MajorTopics), 우측 = 카테고리 지도(TrailMap), 본문 = 글 목록.
 */
export function isSubjectIndexPage(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (path === "/past-exam") return false;
  return (SUBJECT_PATHS as readonly string[]).includes(path);
}

/**
 * 3컬럼 셸을 적용할 모든 페이지 — 이론/코딩 글 + 기출·모의고사 문제 풀이 + 복원 게시판(JCG-2102) + 카테고리 인덱스(JCG-2098).
 * LayoutSwitch 의 단일 게이트(데스크탑일 때만 셸 렌더).
 *
 * 복원 게시판(`/community/restoration`)은 지도·목차·점수가 없는 포럼형이라, 셸 좌측엔 문제 네비,
 * 우측엔 도구+작성 모드(JCG-2065 풀이 변신 차용), 상단 바엔 회차 셀렉터를 주입한다.
 *
 * 주의: 문제 포럼 상세(isForumProblemPage)는 ArticleShell 이 아닌 별도 경량 셸
 * (ForumProblemShell)을 쓰므로 여기 포함하지 않는다.
 */
export function isShellPage(pathname: string): boolean {
  return (
    isArticlePage(pathname) ||
    isExamShellPage(pathname) ||
    isRestorationPage(pathname) ||
    isIndexShellPage(pathname) ||
    isSubjectIndexPage(pathname) ||
    isMyDashboardPage(pathname) ||
    isHomeDashboardPage(pathname) ||
    isSummaryPdfDetailPage(pathname) ||
    isCommunityIssuesPage(pathname) ||
    isCommunityTalkPage(pathname)
  );
}

/**
 * ArticleShell 의 variant="custom" 대상 — 인덱스 셸(/forum/jcg) + /my 통합 대시보드(JCG-2104/2073).
 *
 * 좌/우 사이드바를 페이지(children)가 custom 슬롯에 직접 주입하는 단순 렌더 경로.
 * LayoutSwitch 의 variant 인라인 OR 를 단일화해(JCG-2109) 셸 분기 기준을 route-layout 에 모은다.
 * 홈(/) 대시보드(JCG-2110)도 좌/우 위젯을 페이지가 슬롯 주입하므로 custom 셸이다.
 */
export function isCustomShellPage(pathname: string): boolean {
  return (
    isIndexShellPage(pathname) ||
    isMyDashboardPage(pathname) ||
    isHomeDashboardPage(pathname) ||
    isSummaryPdfDetailPage(pathname) ||
    isCommunityIssuesPage(pathname) ||
    isCommunityTalkPage(pathname)
  );
}

/* =========================================================
   카드형 인덱스 셸 — 카드형 랜딩에 3컬럼 IndexShell 적용 (JCG-2097/2099)
   ========================================================= */

/**
 * 전용 <IndexShell> 컴포넌트(셸 동등 3컬럼 카드 레이아웃)를 적용할 카드형 인덱스 페이지.
 *
 * 글 페이지(isArticlePage)와 달리 본문이 카드 모음이라 좌/우 사이드바에 카드를 분배한다.
 * 데스크탑(≥1024px)에서만 IndexShell 렌더, 그 외(모바일/태블릿)는 기존 세로 스택 폴백.
 *
 * 인덱스 셸 custom(isIndexShellPage, /forum/jcg)과 달리 ArticleShell 이 아니라 전용 IndexShell
 * 로 렌더되며 isShellPage 에는 포함되지 않는다(LayoutSwitch 가 별도 분기). 새 카드형 인덱스 추가
 * 시 이 배열 + 해당 페이지의 IndexShell 슬롯 포털 분기를 함께 둔다. JCG-2099 에서 `/coding` 추가.
 * JCG-2120 에서 `/summary-pdf` 카탈로그(요약 PDF 상품 카드 그리드) 추가 — 좌: 연도 필터 네비,
 * 우: 구매 안내·특전 요약. 정확 일치(includes)라 waitlist 2종·상세 랜딩 등 하위 경로는 미포함.
 *
 * JCG-2164 에서 past-exam 회차 목록(루트 + 연도 인덱스 7개)을 추가 — 좌: 연도/회차 트리 네비,
 * 우: 기출 후기·학습 안내, 본문: 회차 카드 그리드. 슬롯 포털 분기는 `PastExamIndexShell` 이 담당.
 * - 루트 `/past-exam`: isSubjectIndexPage 가 명시 제외(아래)라 isShellPage 에 안 걸려 여기로 분기.
 * - 연도 인덱스 `/past-exam/<YYYY>`(2세그먼트): isExamShellPage(3세그먼트 회차 풀이)·isArticlePage
 *   (past-exam 루트 아님)·isSubjectIndexPage(SUBJECT_PATHS 밖) 어디에도 안 걸려 여기로 분기.
 * - 회차 풀이 `/past-exam/<YYYY>/<slug>`(3세그먼트)는 isExamShellPage 로 ArticleShell — 미포함(건드리지 않음).
 * 정확 일치(includes)라 회차 풀이/후기(-review) 등 3세그먼트 하위 경로는 자동 제외된다.
 */
const CARD_INDEX_SHELL_PATHS = [
  "/theory",
  "/coding",
  "/summary-pdf",
  "/past-exam",
  "/past-exam/2020",
  "/past-exam/2021",
  "/past-exam/2022",
  "/past-exam/2023",
  "/past-exam/2024",
  "/past-exam/2025",
  "/past-exam/2026",
] as const;

export function isCardIndexShellPage(pathname: string): boolean {
  return (CARD_INDEX_SHELL_PATHS as readonly string[]).includes(normalizePath(pathname));
}

/* =========================================================
   문제 포럼 상세 — 경량 셸(ForumProblemShell) 적용 대상 (JCG-2105)
   ========================================================= */

/**
 * URL 세그먼트 안전 디코드 — 깨진 퍼센트 인코딩(`foo%` 등)은 decodeURIComponent 가
 * URIError 를 던지므로, 렌더 중 throw 로 페이지가 죽지 않도록 원본 세그먼트를 그대로 반환한다.
 */
function safeDecodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * 포럼 상세 경로에서 subject/scope/problemId 추출. 관련 문제 네비가 scope 로 관련 문제 목록을
 * 조회하고 현재 problemId 를 강조하는 데 쓴다. 포럼 상세(`/forum/jcg/<subject>/<scope>/<problemId>`,
 * 정확히 5세그먼트)가 아니면 null.
 */
export function parseForumProblemPath(
  pathname: string,
): { subject: string; scope: string; problemId: string } | null {
  const path = normalizePath(pathname);
  const segments = path.split("/").filter(Boolean);
  if (segments[0] !== "forum" || segments[1] !== "jcg" || segments.length !== 5) {
    return null;
  }
  return {
    subject: safeDecodeSegment(segments[2]),
    scope: safeDecodeSegment(segments[3]),
    problemId: safeDecodeSegment(segments[4]),
  };
}

/**
 * 문제 포럼 상세 페이지 여부 — `/forum/jcg/<subject>/<scope>/<problemId>` (정확히 5세그먼트).
 *
 * noindex 내부 도구. ArticleShell(이론/코딩 글·기출 풀이)과 달리 별도 경량 셸을 쓴다
 * (좌: 같은 scope 관련 문제 네비 / 중앙: 문제 + 토론 / 우: 광고 또는 풀이 변신 패널).
 * 따라서 isShellPage 와 분리된 별도 게이트로 LayoutSwitch 에서 직접 판정한다.
 *
 * 판정 규칙은 parseForumProblemPath 와 단일화 — 한 곳만 고치면 게이트·파싱이 함께 갱신된다.
 */
export function isForumProblemPage(pathname: string): boolean {
  return parseForumProblemPath(pathname) !== null;
}

/** 시험장 찾기 페이지 경로 (JCG-2100 전용 셸). */
export const TEST_CENTER_PATH = "/exam-registration/jcg-test-center";

/**
 * 시험장 찾기 전용 3컬럼 셸 페이지 여부 (JCG-2100).
 *
 * 이론/코딩 글(isArticlePage)·기출(isExamShellPage)과 콘텐츠 구성이 완전히 달라
 * (좌: 카드+RedAlert / 가운데: 지도 / 우: 캐루셀) ArticleShell 을 재사용하지 않고
 * TestCenterShell 로 분기한다. 따라서 isShellPage 에는 포함하지 않는다.
 * LayoutSwitch 가 isShellPage 다음으로 이 게이트를 본다.
 */
export function isTestCenterPage(pathname: string): boolean {
  return normalizePath(pathname) === TEST_CENTER_PATH;
}

/**
 * 이론/코딩 경로지만 "이론 학습 글"이 아닌 특수 슬러그 — 문제 풀이/예상문제 가이드/학습 계획.
 * 지도·출석 같은 학습 도구를 노출하지 않는다.
 */
const NON_LEARNING_SLUG_PATTERNS = [
  "gamja-exam",
  "potato-",
  "expected-questions",
  "study-plan",
] as const;

/**
 * 슬러그가 "이론 학습 글"이 아닌 특수 슬러그(문제 풀이/예상문제 가이드/학습 계획)인지.
 * isTheoryLearningPage 내부 판정용.
 */
function isNonLearningSlug(slug: string): boolean {
  return NON_LEARNING_SLUG_PATTERNS.some((pattern) => slug.includes(pattern));
}

/**
 * 지도·출석 등 "이론 학습 도구"가 어울리는 페이지인지 (JCG-2066).
 *
 * 지도(학습 경로)·출석은 이론/코딩 본문 글에서만 의미가 있다. 다음은 셸이 적용돼도 학습 도구를
 * 숨긴다:
 * - 기출·모의고사(isExamShellPage): 회차 문제·해설(-review)·모의고사 — 이론 학습이 아님
 * - exam-registration 안내 콘텐츠: 학습 글이 아닌 시험 접수 안내(THEORY_LEARNING_ROOTS 밖, JCG-2101)
 * - 이론/코딩 경로의 특수 슬러그(문제/예상문제/학습 계획): NON_LEARNING_SLUG_PATTERNS
 *
 * 문제 풀이 페이지는 점수·문제 목록(scoreActive)으로 이미 지도·출석을 대체하지만, 해설/계획/가이드
 * 처럼 RandomProblems 가 없는 페이지는 이 판정으로 지도·출석을 명시적으로 숨긴다.
 */
export function isTheoryLearningPage(pathname: string): boolean {
  if (!isArticlePage(pathname)) return false;
  const path = normalizePath(pathname);
  const segments = path.split("/").filter(Boolean);
  // 학습 도구(지도·출석)는 coding·theory 본문 글에서만 — exam-registration 등 안내성 루트는 제외(JCG-2101)
  if (!(THEORY_LEARNING_ROOTS as readonly string[]).includes(segments[0])) return false;
  const slug = segments.pop() ?? "";
  return !isNonLearningSlug(slug);
}

/**
 * 출석 도구 노출 여부.
 *
 * 데스크탑 셸의 학습 도구 정책과 모바일 플로팅 출석 버튼 정책을 단일화한다.
 * 즉, 기출 회차 풀이·예상문제·학습계획·감자시험 등 데스크탑에서 출석이 숨겨지는
 * 페이지는 모바일에서도 출석 버튼을 마운트하지 않는다.
 */
export function shouldShowAttendanceTool(pathname: string): boolean {
  return isTheoryLearningPage(pathname);
}

/**
 * 글 페이지의 breadcrumb 카테고리 라벨.
 * 3세그먼트는 `/root/cat`, 2세그먼트는 `/root` 라벨을 사용.
 */
export function getArticleCategoryLabel(pathname: string): string {
  const path = normalizePath(pathname);
  const segments = path.split("/").filter(Boolean);
  if (segments.length >= 3) {
    return SUBJECT_LABELS[`/${segments[0]}/${segments[1]}`] ?? "";
  }
  return SUBJECT_LABELS[`/${segments[0]}`] ?? "";
}

/**
 * 글 href → 사람이 읽는 scope 라벨 맵 (JCG-2108).
 *
 * subject-posts.ts 의 모든 SubjectData(코딩·이론)의 recommendedPosts + posts 를 한 번만
 * 펼쳐 모듈 로드 시 1회 구축한다(article-trail.ts 의 SUBJECT_BY_HREF 와 동일 패턴). href 가
 * 여러 과목에 중복 등장해도 같은 scope 라 첫 항목만 남기면 충분.
 */
const SCOPE_BY_HREF: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const subject of [...CODING_SUBJECTS, ...THEORY_SUBJECTS]) {
    for (const post of [...subject.recommendedPosts, ...subject.posts]) {
      if (!(post.href in map)) map[post.href] = post.scope;
    }
  }
  return map;
})();

/**
 * 글 페이지의 breadcrumb 스코프 라벨 (JCG-2108).
 *
 * 현재 경로와 일치하는 글의 사람이 읽는 `scope` 라벨(예: "물리DB 설계", "기초")을 반환한다.
 * 매칭이 없으면(특수 슬러그·2세그먼트 글·기출/모의고사 등) 빈 문자열 → 브레드크럼은 2단 유지.
 *
 * subject-posts.ts 는 순수 데이터 모듈이라 클라이언트 컴포넌트(ContentHeader)에서도 import 가능.
 */
export function getScopeForPath(pathname: string): string {
  return SCOPE_BY_HREF[normalizePath(pathname)] ?? "";
}

/* =========================================================
   카테고리별 최근 학습 페이지(이어학습 모달) — JCG-2166
   ========================================================= */

/** 이어학습 모달이 다루는 학습 카테고리. */
export type StudyCategory = "theory" | "coding" | "past-exam" | "mock-exam";

/** 모달에 고정으로 표시할 카테고리 순서. */
export const STUDY_CATEGORY_ORDER: readonly StudyCategory[] = [
  "theory",
  "coding",
  "past-exam",
  "mock-exam",
] as const;

/** 카테고리 사람이 읽는 라벨. */
export const STUDY_CATEGORY_LABEL: Record<StudyCategory, string> = {
  theory: "이론",
  coding: "코딩",
  "past-exam": "기출",
  "mock-exam": "모의고사",
};

/** 카테고리별 인덱스(폴백) 경로 — 최근 방문 이력이 없을 때 이동 대상. */
export const STUDY_CATEGORY_INDEX: Record<StudyCategory, string> = {
  theory: "/theory",
  coding: "/coding",
  "past-exam": "/past-exam",
  "mock-exam": "/mock-exam",
};

/**
 * 학습 페이지 pathname → 이어학습 카테고리 판정 (JCG-2166).
 *
 * 방문 기록 훅(StudyVisitTracker)이 경로 변경 시 이 게이트로 카테고리를 정해 last page 를
 * upsert 한다. 기존 셸 게이트(isArticlePage·isExamShellPage)를 재사용해 분류 기준을 단일화한다.
 *
 * - 모의고사: `/mock-exam`·`/final-mock-exam`(정확 일치) → "mock-exam" 슬롯 1개를 공유
 *   (둘 중 마지막 방문 1개만 기록). isExamShellPage 가 이 둘도 true 라 기출보다 먼저 분기한다.
 * - 기출: `/past-exam/<YYYY>/<slug>` 회차 풀이 페이지만(isExamShellPage 3세그먼트). 연도
 *   인덱스(카드셸, JCG-2164)·루트 목록은 제외 — "최근 학습"은 실제 문제 풀이 페이지만 기록.
 * - 이론/코딩: 본문 글(isArticlePage) 중 `/theory/*`·`/coding/*`. 같은 ARTICLE_ROOTS 인
 *   exam-registration(시험 접수 안내)은 학습 카테고리가 아니므로 제외된다. 콘텐츠는 반드시
 *   자기 카테고리 디렉터리(`src/app/<category>/contents/`)에 두어야 이 판정이 맞는다(JCG-3542).
 *
 * 학습 페이지가 아니면 null(방문 기록 안 함).
 */
export function getStudyCategory(pathname: string): StudyCategory | null {
  const path = normalizePath(pathname);

  // 모의고사 — isExamShellPage 가 mock 경로도 true 이므로 기출보다 먼저 분기.
  if (isMockExamShellPage(path)) return "mock-exam";

  // 기출 — 3세그먼트 회차 풀이 페이지만(연도 인덱스/루트 제외)
  if (path.startsWith("/past-exam/") && isExamShellPage(path)) return "past-exam";

  // 이론/코딩 본문 글
  if (isArticlePage(path)) {
    if (path.startsWith("/theory/")) return "theory";
    if (path.startsWith("/coding/")) return "coding";
  }

  return null;
}

/** href → 글 제목 맵 (이론/코딩 본문 글, JCG-2166). SCOPE_BY_HREF 와 동일 패턴. */
const TITLE_BY_HREF: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const subject of [...CODING_SUBJECTS, ...THEORY_SUBJECTS]) {
    for (const post of [...subject.recommendedPosts, ...subject.posts]) {
      if (!(post.href in map)) map[post.href] = post.title;
    }
  }
  return map;
})();

/**
 * 학습 페이지 pathname → 이어학습 모달에 표시할 사람이 읽는 제목 (JCG-2166).
 *
 * - 이론/코딩 글: subject-posts 의 글 제목(TITLE_BY_HREF). 없으면(특수 슬러그) 카테고리 라벨.
 * - 기출: `/past-exam/2024/2024-1` → "2024년 1회 기출".
 * - 모의고사: 실전/파이널 모의고사.
 *
 * 방문 기록 시 title 컬럼에 스냅샷으로 저장하고, 모달은 저장값을 우선 표시하되 비어 있으면
 * 이 헬퍼로 폴백한다(과거 레코드 호환).
 */
export function getStudyPageTitle(pathname: string): string {
  const path = normalizePath(pathname);
  const category = getStudyCategory(path);

  if (category === "mock-exam") {
    return isFinalMockExamPage(path) ? "파이널 모의고사" : "실전 모의고사";
  }

  if (category === "past-exam") {
    const segments = path.split("/").filter(Boolean);
    const match = /^(\d{4})-(\d+)$/.exec(segments[2] ?? "");
    return match ? `${match[1]}년 ${match[2]}회 기출` : "기출문제";
  }

  if (category === "theory" || category === "coding") {
    return (
      TITLE_BY_HREF[path] ||
      getArticleCategoryLabel(path) ||
      STUDY_CATEGORY_LABEL[category]
    );
  }

  return "";
}

/**
 * 본문을 넓게 쓰는 페이지 여부 — 사이드바 광고가 콘텐츠를 침범하므로 광고 노출을 차단한다.
 * (데스크탑 클래식 폐지(JCG-3679)로 ContentWrapper 폭 확장은 없어졌고 광고 정책에만 쓰인다.)
 */
export function isWideLayoutPage(pathname: string): boolean {
  return (
    isSubjectPage(pathname) ||
    isErrorNotesPage(pathname) ||
    isCommunityPage(pathname) ||
    isRestorationPage(pathname) ||
    isAdminFullWidth(pathname)
  );
}

/**
 * 사이드바 광고를 숨길 페이지 여부.
 * - max-width 확장 페이지: 콘텐츠 좁아짐 방지
 * - 결제·로그인·랜딩·관리자 등: 광고 노출 부적절
 */
export function isSidebarAdHidden(pathname: string): boolean {
  if (isExamInfoPage(pathname)) return true;
  if (isWideLayoutPage(pathname)) return true;
  if ((SIDEBAR_AD_HIDDEN_EXTRA_PATHS as readonly string[]).includes(pathname)) return true;
  if (SIDEBAR_AD_HIDDEN_EXTRA_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true;
  return false;
}
